import React, {ComponentType} from 'react';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';
import {markAsSafe} from 'app/utils/strings';

const makeStepTitleIcon = (IconComponent: ComponentType<any>, name: string) => {
  return function StepTitleIcon() {
    return (
      <LazyTooltip tooltip={name}>
        <IconComponent width="2em" height="2em" />
      </LazyTooltip>
    );
  };
};

const makeInline = (IconComponent: ComponentType<any>) => {
  return function InlineIcon({fill = '#000'}: {fill?: string}) {
    return (
      <IconComponent
        style={{
          verticalAlign: 'text-bottom',
        }}
        width="1.2rem"
        height="1.2em"
        fill={fill}
      />
    );
  };
};

/* exclusive gateway */
export const ExclusiveGatewayIcon = React.forwardRef<null, HTMLElement>(
  (props: any, ref) => (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      aria-hidden="true"
      style={{
        msTransform: 'rotate(360deg)',
        WebkitTransform: 'rotate(360deg)',
        transform: 'rotate(360deg)',
        display: 'inline-flex',
      }}
      preserveAspectRatio="xMidYMid meet"
      viewBox="0 0 2048 2048"
      {...props}
    >
      <path d="M1024.022 99.36c-19.324-.017-38.646 7.15-52.98 21.55L120.937 971.023c-28.67 28.668-28.537 77.295.132 105.963l849.971 849.965c28.67 28.678 77.294 28.804 105.963 0l850.106-850.1c28.669-28.667 28.536-77.296-.135-105.964L1077.002 120.91c-14.334-14.334-33.657-21.534-52.98-21.55zm-.065 126.045l798.66 798.666-798.66 798.657-798.66-798.657 798.66-798.666zM725.686 669.792c-.014 0-9.612 1.838-9.62 1.838-.01 0-8.144 5.513-8.15 5.513l-30.732 30.739c-.01 0-5.61 8.225-5.614 8.322 0 .01-1.737 9.48-1.736 9.48 0 .01 1.868 9.385 1.871 9.385 0 .01 5.338 8.322 5.344 8.322l280.707 280.7-280.572 280.574v-.088c0 .01-5.61 8.32-5.614 8.32 0 .01-1.736 9.483-1.736 9.483 0 .02 1.868 9.385 1.871 9.385 0 0 5.339 8.223 5.344 8.32l30.734 30.728c.01.01 8.411 5.516 8.418 5.516.01 0 9.346 1.838 9.354 1.838.01 0 9.479-1.74 9.486-1.74.01 0 8.28-5.614 8.285-5.614l280.576-280.582 280.637 280.641c.01.01 8.412 5.516 8.418 5.516.01 0 9.346 1.838 9.354 1.838.01 0 9.48-1.743 9.488-1.743.01 0 8.276-5.61 8.281-5.61l30.735-30.73c.01-.01 5.475-8.126 5.48-8.126 0-.01 1.871-9.58 1.871-9.676 0-.01-1.869-9.385-1.873-9.385 0 0-5.472-8.418-5.478-8.418l-280.606-280.611 280.608-280.604c.01 0 5.473-8.127 5.478-8.127 0-.01 1.871-9.578 1.871-9.578 0-.02-1.868-9.385-1.87-9.385 0-.01-5.606-8.322-5.612-8.322l-30.735-30.738c-.01 0-8.143-5.514-8.15-5.514-.01 0-9.345-1.84-9.353-1.84-.01 0-9.613 1.84-9.62 1.84-.01 0-8.145 5.514-8.15 5.514l-280.613 280.613-280.739-280.748v-.088c-.01 0-8.278-5.32-8.285-5.32-.01 0-9.34-1.837-9.351-1.838h-.002z" />
    </svg>
  )
);
export const StepTitleExclusiveGatewayIcon = makeStepTitleIcon(
  ExclusiveGatewayIcon,
  gt('Decision')
);
export const InlineExclusiveGatewayIcon = makeInline(ExclusiveGatewayIcon);

/* ending */
export const EndingIcon = React.forwardRef<null, HTMLElement>(
  (props: any, ref) => (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      aria-hidden="true"
      preserveAspectRatio="xMidYMid meet"
      viewBox="0 0 200 200"
      {...props}
    >
      <style
        dangerouslySetInnerHTML={markAsSafe('.inner-circle {fill: #FFF;}')}
      />
      <circle r="90" cy="100" cx="100" fill="#CC0000" />
      <circle className="inner-circle" r="75" cy="100" cx="100" />
    </svg>
  )
);
export const StepTitleEndingIcon = makeStepTitleIcon(EndingIcon, gt('End'));
export const InlineEndingIcon = makeInline(EndingIcon);

/* process procedure */
const procedureCt = getContentType('procedure');
export const ProcessProcedureIcon = React.forwardRef<null, HTMLElement>(
  (props: any, ref) => (
    <i
      className={procedureCt.icon}
      style={{
        fontSize: '1.5em',
      }}
      {...props}
      ref={ref}
    />
  )
);
export const ProcessProcedureTitleIcon = makeStepTitleIcon(
  ProcessProcedureIcon,
  gt('Procedure')
);
export const InlineProcessProcedureIcon = makeInline(ProcessProcedureIcon);
